import { getApps, initializeApp } from "firebase/app";
import {
  initializeAuth,
  inMemoryPersistence,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { getExternalAuth } from "./firebase";
import { siteRequest, AccessError } from "./siteCustomerApi";
let auth;
export function getWebsiteGoogleAuth() {
  if (auth) return auth;
  const configured = getExternalAuth();
  if (!configured)
    throw new Error(
      "Google sign-in is awaiting website identity configuration. Use email sign-in when available.",
    );
  const name = "MAGELLAN_WEBSITE_CUSTOMER_GOOGLE",
    app =
      getApps().find((a) => a.name === name) ||
      initializeApp(configured.app.options, name);
  auth = initializeAuth(app, {
    persistence: inMemoryPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
  });
  return auth;
}
export async function signInWebsiteGoogle() {
  const identity = getWebsiteGoogleAuth(),
    provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  try {
    const result = await signInWithPopup(identity, provider);
    const token = await result.user.getIdToken();
    return await siteRequest("/auth/google", {
      method: "POST",
      body: {},
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    if (error instanceof AccessError) throw error;
    if (
      error.code === "auth/popup-closed-by-user" ||
      error.code === "auth/cancelled-popup-request"
    )
      throw new Error("Google sign-in was cancelled. You can try again.");
    if (error.code === "auth/popup-blocked")
      throw new Error("Allow the Google sign-in popup, then try again.");
    if (
      [
        "auth/unauthorized-domain",
        "auth/operation-not-allowed",
        "auth/invalid-api-key",
      ].includes(error.code)
    )
      throw new Error(
        "Google sign-in is awaiting website configuration. Please use an email link when available.",
      );
    throw new Error(
      "Google sign-in could not connect. Please try again or use an email link.",
    );
  } finally {
    await signOut(identity).catch(() => {});
  }
}

// Golden-thread demo accounts only (a password identity the demo loader registered). The build
// flag is set only by the local live-data server; the server also refuses everything else.
export const demoSignInAvailable = () => process.env.REACT_APP_SITE_DEMO_SIGNIN === "true";
let demoAuth;
export async function signInWebsiteDemo(email, password) {
  if (!demoSignInAvailable()) throw new Error("Demo sign-in is not available here.");
  const configured = getExternalAuth();
  if (!configured) throw new Error("Demo sign-in is awaiting website identity configuration.");
  if (!demoAuth) {
    const name = "MAGELLAN_WEBSITE_CUSTOMER_DEMO",
      app = getApps().find((a) => a.name === name) || initializeApp(configured.app.options, name);
    demoAuth = initializeAuth(app, { persistence: inMemoryPersistence });
  }
  try {
    const result = await signInWithEmailAndPassword(demoAuth, email, password);
    const token = await result.user.getIdToken();
    return await siteRequest("/auth/demo", { method: "POST", body: {}, headers: { Authorization: `Bearer ${token}` } });
  } catch (error) {
    if (error instanceof AccessError) throw error;
    if (["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found", "auth/invalid-email"].includes(error.code))
      throw new Error("That demo email and password do not match.");
    throw new Error("Demo sign-in could not connect. Please try again.");
  } finally {
    await signOut(demoAuth).catch(() => {});
  }
}
